import { AlertTriangle, Bot } from 'lucide-react';

export interface ResolutionSourceCounts {
  automation: number;
  agent: number;
  unknown: number;
}

interface ResolutionSourceWidgetProps {
  title?: string;
  data?: ResolutionSourceCounts;
  loading?: boolean;
  className?: string;
}

/**
 * "Resolved By" — share of COMPLETED tickets closed out by CentraOne
 * (automation rules) and by agents.
 *
 * Form: BOTH percentages stated as figures, plus a single two-segment split
 * bar — not a donut. With only two categories a donut makes the reader compare
 * arc angles; the numbers answer it directly and the bar carries the
 * part-to-whole. Each share is named next to its own number so neither has to
 * be inferred as "the rest", and the layout absorbs a third segment later
 * without a redesign.
 *
 * Colors are validated for CVD separation in BOTH modes (see SERIES below) —
 * violet/cyan, worst-case ΔE 17.1 deutan on white, 12.6 on the dark surface.
 *
 * `unknown` (tickets completed before attribution shipped, or by a cron path
 * with no actor) is deliberately EXCLUDED from the percentages and the bar, and
 * disclosed underneath instead — folding it into either real bucket would
 * misattribute work, and silently dropping it would overstate the sample.
 */

// Series colors, in the project's own tokens. The split this widget draws is
// machine-versus-human, which is exactly what `--co-machine` exists to say, so
// automation takes it and the agent share takes plain ink. Both tokens carry
// their own dark-mode values, which is why the old light/dark pair is gone: the
// theme already steps the colour down, so the widget no longer has to.
//
// `automation` is labelled "CentraOne" in the UI — the tenant sees the product
// resolving the ticket, not the internal mechanism. The data key stays
// `automation` to match the backend attribution signal.
const SERIES = {
  automation: { label: 'CentraOne', token: 'var(--co-machine)' },
  agent: { label: 'Agent', token: 'var(--co-ink)' },
} as const;

function formatCount(n: number): string {
  return n.toLocaleString();
}

export function ResolutionSourceWidget({
  title,
  data,
  loading = false,
  className = '',
}: ResolutionSourceWidgetProps) {
  const shell = `border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`;

  if (loading) {
    return (
      <div className={shell}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="h-5 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="flex-1 min-h-0 px-4 py-4 space-y-3">
          <div className="h-8 w-24 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-3 w-full bg-gray-100 dark:bg-gray-700 rounded-full animate-pulse" />
          <div className="h-4 w-2/3 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={shell}>
        <div className="flex flex-col items-center justify-center text-center flex-1 p-4">
          <AlertTriangle className="h-8 w-8 text-red-400 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Failed to load resolution data</p>
        </div>
      </div>
    );
  }

  const { automation, agent, unknown } = data;
  // Attributed total only — `unknown` is reported separately, never divided into.
  const attributed = automation + agent;
  const automationPct = attributed > 0 ? (automation / attributed) * 100 : 0;
  const agentPct = attributed > 0 ? 100 - automationPct : 0;

  if (attributed === 0) {
    return (
      <div className={shell}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title || 'Resolved By'}</h3>
        </div>
        <div className="flex flex-col items-center justify-center text-center flex-1 p-4">
          <Bot className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nothing attributed yet</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {unknown > 0
              ? `${formatCount(unknown)} ticket${unknown === 1 ? '' : 's'} completed before resolution tracking started`
              : 'No tickets completed in this period'}
          </p>
        </div>
      </div>
    );
  }

  const segments = [
    { key: 'automation' as const, value: automation, pct: automationPct },
    { key: 'agent' as const, value: agent, pct: agentPct },
  ].filter((s) => s.value > 0);

  return (
    <div className={shell}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title || 'Resolved By'}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {formatCount(attributed)} completed ticket{attributed === 1 ? '' : 's'}
        </p>
      </div>

      <div className="flex-1 min-h-0 px-4 py-3 flex flex-col justify-center gap-3">
        {/* Both shares as figures. Neither side is left to be inferred as "the
            rest", which is what a single hero number forces the reader to do.
            Each column reads name → percentage top-down, so the label is fixed
            before the figure it belongs to; the bar underneath carries the
            part-to-whole. */}
        <div className="flex items-end gap-6">
          {(['automation', 'agent'] as const).map((key) => (
            <div key={key}>
              {/* Swatch sits with the label here rather than in a separate
                  legend row — one label per series, so the color binding
                  travels with the number it belongs to. Raw counts stay out:
                  the header already states the total, and the hover title on
                  each bar segment carries the per-series count. */}
              <p className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: SERIES[key].token }}
                />
                {SERIES[key].label}
              </p>
              <p className="mt-1.5 text-3xl font-bold leading-none text-gray-900 dark:text-white">
                {(key === 'automation' ? automationPct : agentPct).toFixed(0)}%
              </p>
            </div>
          ))}
        </div>

        {/* Split bar. `gap-[2px]` is the surface gap that separates the two
            segments — no borders/strokes, which would add non-data ink. Rounded
            outer ends only, so the pair reads as one bar. */}
        <div
          className="flex gap-[2px] h-3 rounded-full overflow-hidden"
          role="img"
          aria-label={`${automationPct.toFixed(0)}% resolved by CentraOne (${formatCount(automation)} tickets), ${agentPct.toFixed(0)}% resolved by agents (${formatCount(agent)} tickets)`}
        >
          {segments.map((s) => (
            <div
              key={s.key}
              // The token carries its own dark value, so the bar follows the
              // theme toggle in CSS with no JS and no re-render — the old
              // light/dark var pair is no longer needed. Width is inline
              // because it is data.
              className="h-full first:rounded-l-full last:rounded-r-full"
              style={{ width: `${s.pct}%`, background: SERIES[s.key].token }}
              title={`${SERIES[s.key].label}: ${formatCount(s.value)} (${s.pct.toFixed(1)}%)`}
            />
          ))}
        </div>

        {unknown > 0 && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            Excludes {formatCount(unknown)} ticket{unknown === 1 ? '' : 's'} with no recorded resolver
          </p>
        )}
      </div>
    </div>
  );
}
